// Server base URL accessor — reads VITE_SERVER_BASE_URL, strips trailing slashes.
//
// Also the environment accessor: getServerEnv derives the deployment from that
// same URL, and getServerAuthorizationLabel formats it through
// @synvya/auth-identity, which masters the label string.

import {
  buildDinersEventUrl,
  buildDinersHappyHourUrl,
  buildDinersRestaurantUrl,
} from '@synvya/capabilities'
import {
  serverAuthorizationLabel,
  serverEnvFromBaseUrl,
} from '@synvya/auth-identity/server-registration'

export function getServerBaseUrl(): string {
  const base = import.meta.env.VITE_SERVER_BASE_URL as string | undefined
  if (!base) throw new Error('Missing VITE_SERVER_BASE_URL environment variable')
  return base.replace(/\/+$/, '')
}

export function getKeycastUrl(): string {
  const url = import.meta.env.VITE_KEYCAST_URL as string | undefined
  if (!url) throw new Error('Missing VITE_KEYCAST_URL environment variable')
  return url.replace(/\/+$/, '')
}

export function getDefaultRelays(): string[] {
  const raw = import.meta.env.VITE_NOSTR_RELAYS as string | undefined
  if (!raw) {
    console.warn('[relay] VITE_NOSTR_RELAYS not set — no relays configured')
    return []
  }
  const relays = raw.split(',').map((r) => r.trim()).filter(Boolean)
  if (relays.length === 0) {
    console.warn('[relay] VITE_NOSTR_RELAYS is empty after parsing')
  }
  return relays
}

/**
 * The environment this build talks to, derived from the server it is
 * configured against.
 *
 * **`VITE_SERVER_BASE_URL` is the one input.** It is already required, so the
 * environment cannot be unset while the rest of the app works. `VITE_APP_ENV`
 * stays honoured as an explicit declaration, and a value that disagrees with
 * the server URL throws — that disagreement is the defect, not a preference.
 *
 * There is no default. A previous `|| 'staging'` labelled every local build
 * and every production build `Synvya Server (staging)`; the production half is
 * [account#225](https://github.com/Synvya/account/issues/225).
 */
export function getServerEnv(): string {
  const derived = serverEnvFromBaseUrl(getServerBaseUrl())
  const declared = import.meta.env.VITE_APP_ENV as string | undefined
  if (declared && declared !== derived) {
    throw new Error(
      `VITE_APP_ENV is "${declared}" but VITE_SERVER_BASE_URL points at ${derived}. ` +
        'Fix whichever is wrong — a mismatched pair registers restaurants against ' +
        'the wrong environment.',
    )
  }
  return derived
}

/** The Keycast authorization label this portal mints for the always-on server. */
export function getServerAuthorizationLabel(): string {
  return serverAuthorizationLabel(getServerEnv())
}

/**
 * Synvya operator pubkeys — used to discover curated template calendars
 * (NIP-51 list of calendars at `d=synvya-template-calendars` per operator).
 * Parses comma-separated `VITE_SYNVYA_OPERATOR_PUBKEYS`. Returns [] when
 * not configured; consumers should degrade gracefully (Events page hides
 * the Suggested section).
 */
export function getSynvyaOperatorPubkeys(): string[] {
  const raw = import.meta.env.VITE_SYNVYA_OPERATOR_PUBKEYS as string | undefined
  if (!raw) return []
  return raw
    .split(',')
    .map((pk) => pk.trim().toLowerCase())
    .filter(Boolean)
}

/** Lambda API base URL (discovery publish, billing, etc.). Null if not configured. */
export function getBaseUrlOrNull(): string | null {
  const base = import.meta.env.VITE_BASE_URL as string | undefined
  if (!base) return null
  return base.replace(/\/+$/, '')
}

// Diners app origin derivation — maps this account portal's host to its
// sibling diners host by swapping the `account.` subdomain for `diners.`
// (and the dev port 5173 → 5174). Covers prod / staging / local uniformly:
//   account.synvya.com            → https://diners.synvya.com
//   account.staging.synvya.com    → https://diners.staging.synvya.com
//   account.local.synvya.com:5173 → http://diners.local.synvya.com:5174
// Protocol is inherited from the current location. Used by LoyaltyHomePage
// to build /restaurant/<pubkey> deep-links.

export function getDinersOrigin(): string {
  const { protocol, hostname, port } = window.location
  const dinersHost = hostname.replace(/^account\./, 'diners.')
  const dinersPort = port === '5173' ? '5174' : port
  return `${protocol}//${dinersHost}${dinersPort ? `:${dinersPort}` : ''}`
}

/**
 * Deep-link to a restaurant's discovery page in the diners app.
 *
 * **The path comes from `@synvya/capabilities`**, like the two promotion
 * builders below it. `diners` owns the route and builds the same link; a path
 * spelled here as well is one that breaks silently the day that route changes.
 */
export function getDinersRestaurantUrl(pubkey: string): string {
  return buildDinersRestaurantUrl({ host: getDinersOrigin(), pubkey })
}

// Per-promotion share links — thin wrappers over the shared @synvya/capabilities
// builders (which own the `?hh=`/`?event=` shape + naddr encoding), passing this
// env's diners origin as the host. Mirrors getDinersRestaurantUrl above; the
// diners app resolves the naddr by its decoded coordinate. `naddr` is a bare
// `naddr1…` (or `nostr:`-prefixed — the builder strips it).

/** Deep-link to a restaurant with one calendar event highlighted (addressable naddr). */
export function getDinersEventUrl(pubkey: string, naddr: string): string {
  return buildDinersEventUrl({ host: getDinersOrigin(), pubkey, naddr })
}

/** Deep-link to a restaurant with one happy hour highlighted (addressable naddr). */
export function getDinersHappyHourUrl(pubkey: string, naddr: string): string {
  return buildDinersHappyHourUrl({ host: getDinersOrigin(), pubkey, naddr })
}

